'use client';

import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { createBuyerRequest } from '@/lib/buyerRequests';

const ukCounties = [
  'Bedfordshire','Berkshire','Bristol','Buckinghamshire','Cambridgeshire','Cheshire',
  'City of London','Cornwall','Cumbria','Derbyshire','Devon','Dorset','Durham',
  'East Riding of Yorkshire','East Sussex','Essex','Gloucestershire','Greater London',
  'Greater Manchester','Hampshire','Herefordshire','Hertfordshire','Isle of Wight',
  'Kent','Lancashire','Leicestershire','Lincolnshire','Merseyside','Norfolk',
  'North Yorkshire','Northamptonshire','Northumberland','Nottinghamshire','Oxfordshire',
  'Rutland','Shropshire','Somerset','South Yorkshire','Staffordshire','Suffolk',
  'Surrey','Tyne and Wear','Warwickshire','West Midlands','West Sussex',
  'West Yorkshire','Wiltshire','Worcestershire',
  'Aberdeenshire','Angus','Argyll and Bute','Clackmannanshire','Dumfries and Galloway',
  'Dundee','East Ayrshire','East Lothian','Edinburgh','Falkirk','Fife','Glasgow',
  'Highland','Inverclyde','Midlothian','Moray','North Ayrshire','North Lanarkshire',
  'Orkney Islands','Perth and Kinross','Renfrewshire','Scottish Borders','Shetland Islands',
  'South Ayrshire','South Lanarkshire','Stirling','West Dunbartonshire','West Lothian',
  'Western Isles',
  'Anglesey','Blaenau Gwent','Bridgend','Caerphilly','Cardiff','Carmarthenshire',
  'Ceredigion','Conwy','Denbighshire','Flintshire','Gwynedd','Merthyr Tydfil',
  'Monmouthshire','Neath Port Talbot','Newport','Pembrokeshire','Powys',
  'Rhondda Cynon Taf','Swansea','Torfaen','Vale of Glamorgan','Wrexham',
  'Antrim','Armagh','Down','Fermanagh','Londonderry','Tyrone',
];

const stockCategories = [
  { label: 'Cattle', value: 'cattle' },
  { label: 'Sheep', value: 'sheep' },
  { label: 'Pigs', value: 'pigs' },
  { label: 'Horses & Ponies', value: 'horses' },
  { label: 'Goats', value: 'goats' },
  { label: 'Poultry', value: 'poultry' },
  { label: 'Deer', value: 'deer' },
  { label: 'Alpacas & Llamas', value: 'alpacas' },
  { label: 'Machinery', value: 'machinery' },
  { label: 'Deadstock / By-products', value: 'deadstock' },
  { label: 'Other', value: 'other' },
];

const urgencyOptions = ['Flexible — no rush', 'Within 1 month', 'Within 2 weeks', 'Urgent — within a week'];

const budgetOptions = [
  'Open / Negotiable',
  'Under £500',
  '£500 – £1,000',
  '£1,000 – £2,500',
  '£2,500 – £5,000',
  '£5,000 – £10,000',
  '£10,000 – £25,000',
  '£25,000+',
];

interface BuyerRequestForm {
  title: string;
  category: string;
  breed: string;
  quantity: string;
  ageRange: string;
  weightRange: string;
  sex: string;
  budget: string;
  county: string;
  radius: string;
  urgency: string;
  assurance: string;
  additionalRequirements: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  termsAgreed: boolean;
  contactConsent: boolean;
}

const emptyForm: BuyerRequestForm = {
  title: '',
  category: '',
  breed: '',
  quantity: '',
  ageRange: '',
  weightRange: '',
  sex: '',
  budget: '',
  county: '',
  radius: '',
  urgency: '',
  assurance: '',
  additionalRequirements: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  termsAgreed: false,
  contactConsent: false,
};


// The form offers budget bands rather than free numbers, so turn the chosen band
// back into the min/max the database stores.
function parseBudget(band: string): { min: number | null; max: number | null } {
  if (!band || band.startsWith('Open')) return { min: null, max: null };
  const numbers = band.match(/[\d,]+/g)?.map((n) => Number(n.replace(/,/g, ''))) ?? [];
  if (band.startsWith('Under')) return { min: null, max: numbers[0] ?? null };
  if (band.includes('+')) return { min: numbers[0] ?? null, max: null };
  if (numbers.length >= 2) return { min: numbers[0], max: numbers[1] };
  return { min: numbers[0] ?? null, max: null };
}

// Livestock categories double as the species; machinery and deadstock do not.
const LIVESTOCK = new Set(['cattle', 'sheep', 'pigs', 'horses', 'goats', 'poultry', 'deer', 'alpacas']);

function FormField({ label, required, children, hint }: { label: string; required?: boolean; children: React.ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-semibold text-foreground">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function SectionHeading({ title, iconName }: { title: string; iconName: string }) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-border mb-4">
      <Icon name={iconName} size={18} className="text-primary shrink-0" />
      <h3 className="text-base font-bold text-foreground">{title}</h3>
    </div>
  );
}

export default function BuyerRequestSection() {
  const [form, setForm] = useState<BuyerRequestForm>(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supabase = useMemo(() => createClient(), []);
  const { user } = useAuth();

  const set = (field: keyof BuyerRequestForm, value: string | boolean) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);

    if (!user) {
      setError('Please sign in or create a free account first — your request is saved against your account so you can manage the replies.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    try {
      const budget = parseBudget(form.budget);
      const quantity = Number(String(form.quantity).replace(/[^\d]/g, ''));

      await createBuyerRequest(supabase, {
        category: form.category,
        title: form.title.trim(),
        description: form.additionalRequirements.trim() || null,
        species: LIVESTOCK.has(form.category) ? form.category : null,
        breed: form.breed.trim() || null,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : null,
        county: form.county || null,
        budget_min: budget.min,
        budget_max: budget.max,
        details: {
          ageRange: form.ageRange,
          weightRange: form.weightRange,
          sex: form.sex,
          radius: form.radius,
          urgency: form.urgency,
          assurance: form.assurance,
        },
        contact_name: form.contactName.trim() || null,
        contact_email: form.contactEmail.trim() || null,
        contact_phone: form.contactPhone.trim() || null,
      });

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not post your request. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (submitted) {
    return (
      <div className="card text-center py-12 px-6">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircleIcon" size={36} className="text-green-600" />
        </div>
        <h2 className="text-xl font-extrabold text-foreground mb-2">Buyer Request Submitted!</h2>
        <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">
          Your wanted listing has been saved and will be reviewed within 48 hours. Once it is live, sellers who have what you are after can respond through the site — your phone number and email stay private.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={() => { setForm(emptyForm); setSubmitted(false); }}
            className="btn-primary px-8 py-2.5"
          >
            <Icon name="PlusCircleIcon" size={18} />
            Post Another Request
          </button>
          <Link href="/buyer-requests" className="btn-secondary px-8 py-2.5 justify-center">
            <Icon name="ClipboardDocumentListIcon" size={18} />
            See the wanted board
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-5 py-4">
        <Icon name="InformationCircleIcon" size={20} className="text-blue-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-blue-900 mb-0.5">Buyer Request / Wanted</p>
          <p className="text-sm text-blue-800">
            Can't find what you're looking for? Post a buyer request and let sellers come to you. Your request will be visible to verified sellers on FarmTradeDirect.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4" role="alert">
          <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <p>{error}</p>
            {!user && (
              <Link href="/sign-up-login-screen" className="font-bold underline mt-1 inline-block">
                Sign in or create a free account
              </Link>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* What are you looking for */}
        <div className="card">
          <SectionHeading title="What Are You Looking For?" iconName="MagnifyingGlassIcon" />
          <div className="space-y-4">
            <FormField label="Request Title" required hint='e.g. "Wanted: 20 Limousin x Heifers, store weight"'>
              <input
                className="form-input"
                placeholder='e.g. Wanted: 20 Limousin x Heifers, store weight'
                value={form.title}
                onChange={e => set('title', e.target.value)}
                required
              />
            </FormField>
            <FieldRow>
              <FormField label="Category" required>
                <select
                  className="form-input"
                  value={form.category}
                  onChange={e => set('category', e.target.value)}
                  required
                >
                  <option value="">Select category…</option>
                  {stockCategories.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Breed / Type" hint="Leave blank if any breed considered">
                <input
                  className="form-input"
                  placeholder="e.g. Limousin x, Texel, Any"
                  value={form.breed}
                  onChange={e => set('breed', e.target.value)}
                />
              </FormField>
            </FieldRow>
            <FieldRow>
              <FormField label="Quantity Required" required hint="Number of head / units">
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  placeholder="e.g. 20"
                  value={form.quantity}
                  onChange={e => set('quantity', e.target.value)}
                  required
                />
              </FormField>
              <FormField label="Sex" hint="Leave blank if no preference">
                <select
                  className="form-input"
                  value={form.sex}
                  onChange={e => set('sex', e.target.value)}
                >
                  <option value="">No preference</option>
                  {['Heifers','Steers','Bulls','Cows','Ewes','Rams','Lambs','Mixed','Gilts','Boars','Sows'].map(s => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </FormField>
            </FieldRow>
            <FieldRow>
              <FormField label="Age / Age Range" hint="e.g. 18–24 months, or leave blank">
                <input
                  className="form-input"
                  placeholder="e.g. 18–24 months"
                  value={form.ageRange}
                  onChange={e => set('ageRange', e.target.value)}
                />
              </FormField>
              <FormField label="Weight Range (kg)" hint="e.g. 350–450 kg, or leave blank">
                <input
                  className="form-input"
                  placeholder="e.g. 350–450 kg"
                  value={form.weightRange}
                  onChange={e => set('weightRange', e.target.value)}
                />
              </FormField>
            </FieldRow>
            <FieldRow>
              <FormField label="Assurance / Health Requirements" hint="e.g. Red Tractor, TB clear, BVD tested">
                <input
                  className="form-input"
                  placeholder="e.g. Red Tractor, TB clear within 60 days"
                  value={form.assurance}
                  onChange={e => set('assurance', e.target.value)}
                />
              </FormField>
              <FormField label="Budget">
                <select
                  className="form-input"
                  value={form.budget}
                  onChange={e => set('budget', e.target.value)}
                >
                  <option value="">Select budget range…</option>
                  {budgetOptions.map(b => <option key={b}>{b}</option>)}
                </select>
              </FormField>
            </FieldRow>
            <FormField label="Additional Requirements / Description" hint="Describe exactly what you need — the more detail, the better the responses">
              <textarea
                className="form-input min-h-[100px]"
                placeholder="Include any specific requirements: purpose (breeding, finishing, show), health history expectations, delivery preferences, etc."
                value={form.additionalRequirements}
                onChange={e => set('additionalRequirements', e.target.value)}
              />
            </FormField>
          </div>
        </div>

        {/* Location & Timing */}
        <div className="card">
          <SectionHeading title="Location & Timing" iconName="MapPinIcon" />
          <div className="space-y-4">
            <FieldRow>
              <FormField label="Your County / Region" required>
                <select
                  className="form-input"
                  value={form.county}
                  onChange={e => set('county', e.target.value)}
                  required
                >
                  <option value="">Select county…</option>
                  {ukCounties.map(c => <option key={c}>{c}</option>)}
                </select>
              </FormField>
              <FormField label="Maximum Travel / Collection Radius" hint="How far are you willing to travel or collect from?">
                <select
                  className="form-input"
                  value={form.radius}
                  onChange={e => set('radius', e.target.value)}
                >
                  <option value="">Select radius…</option>
                  {['Within 25 miles','Within 50 miles','Within 100 miles','Within 150 miles','Nationwide','Will arrange haulage'].map(r => (
                    <option key={r}>{r}</option>
                  ))}
                </select>
              </FormField>
            </FieldRow>
            <FormField label="When Do You Need Them?">
              <select
                className="form-input"
                value={form.urgency}
                onChange={e => set('urgency', e.target.value)}
              >
                <option value="">Select urgency…</option>
                {urgencyOptions.map(u => <option key={u}>{u}</option>)}
              </select>
            </FormField>
          </div>
        </div>

        {/* Contact Details */}
        <div className="card">
          <SectionHeading title="Your Contact Details" iconName="UserCircleIcon" />
          <div className="space-y-4">
            <FieldRow>
              <FormField label="Full Name" required>
                <input
                  className="form-input"
                  placeholder="Your full name"
                  value={form.contactName}
                  onChange={e => set('contactName', e.target.value)}
                  required
                />
              </FormField>
              <FormField label="Telephone Number" required>
                <input
                  className="form-input"
                  type="tel"
                  placeholder="e.g. 07700 900000"
                  value={form.contactPhone}
                  onChange={e => set('contactPhone', e.target.value)}
                  required
                />
              </FormField>
            </FieldRow>
            <FormField label="Email Address" required>
              <input
                className="form-input"
                type="email"
                placeholder="your@email.com"
                value={form.contactEmail}
                onChange={e => set('contactEmail', e.target.value)}
                required
              />
            </FormField>
            <p className="text-xs text-muted-foreground">
              Your contact details will only be shared with verified sellers who respond to your request. They will not be publicly displayed.
            </p>
          </div>
        </div>

        {/* Review notice */}
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
          <Icon name="ClockIcon" size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <span className="font-bold">48 hour review period:</span> All buyer requests are reviewed by hand by the FarmTradeDirect team before going live. You will be notified by email once your request is published.
          </p>
        </div>

        {/* Terms & Submit */}
        <div className="card">
          <SectionHeading title="Terms & Submission" iconName="DocumentTextIcon" />
          <div className="space-y-3 mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                required
                checked={form.termsAgreed}
                onChange={e => set('termsAgreed', e.target.checked)}
                className="mt-0.5 w-5 h-5 accent-primary shrink-0"
              />
              <span className="text-sm text-foreground">
                I confirm the information provided is accurate and I agree to the{' '}
                {/* New tab deliberately — navigating away would lose the form. */}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline">FarmTradeDirect Terms &amp; Conditions</a>
                {' '}and{' '}
                {/* TODO: no Listing Policy page exists yet — see PostListingScreen. */}
                <a href="#" className="text-primary underline">Listing Policy</a>.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.contactConsent}
                onChange={e => set('contactConsent', e.target.checked)}
                className="mt-0.5 w-5 h-5 accent-primary shrink-0"
              />
              <span className="text-sm text-foreground">
                I consent to FarmTradeDirect sharing my contact details with verified sellers who respond to this request.
              </span>
            </label>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 sm:flex-none sm:px-10 py-3 text-base font-bold disabled:opacity-60"
            >
              <Icon name="PaperAirplaneIcon" size={20} />
              {saving ? 'Posting…' : 'Submit Buyer Request'}
            </button>
            <button
              type="button"
              onClick={() => setForm(emptyForm)}
              className="btn-secondary py-3"
            >
              <Icon name="ArrowPathIcon" size={18} />
              Clear Form
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
            <Icon name="ClockIcon" size={13} className="text-muted-foreground shrink-0" />
            Requests are reviewed within 48 hours. You will receive an email confirmation once your request is live.
          </p>
        </div>
      </form>
    </div>
  );
}
